import { useLanguage } from '../i18n/useLanguage';
import { trackStoreClick } from '../lib/analytics';
import { appStoreUrl, googlePlayUrl } from '../lib/storeLinks';

/**
 * Public landing page rendered at `/` while the full preview app
 * (Header + sections + Footer) is parked behind `/preview/*`.
 *
 * Visual recipe is a stripped-down clone of HeroSection:
 *   • Same cream `bg-tan` scene with the hero pattern overlay,
 *     bottom white fade, and the two side-character props that
 *     loop on `animate-float-y` (one offset by -2s so they drift
 *     out of phase).
 *   • Foreground column shows ONLY:
 *       1. The "I LOVE COOKING" logo mark.
 *       2. The cream pill title (fork-spoon icons + headline)
 *          but with `t('hero.underConstruction')` text instead
 *          of the subtitle CTA copy.
 *       3. Both store badge buttons (App Store + Google Play).
 *   • No <Header />, no FloatingSocialRail / FloatingCTARail,
 *     no Footer, no video, no scroll snap, no other sections.
 *
 * Kept self-contained (no shared layout component) so the
 * full preview app can keep evolving independently without
 * dragging this placeholder along with it.
 */
export default function UnderConstruction() {
  const { lang, t } = useLanguage();

  return (
    <main className="relative isolate flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-tan px-4 py-10">
      {/* Background pattern overlay (Figma node 1:3514) — same
          asset HeroSection uses, behind everything. */}
      <img
        src="/images/hero-bg-pattern.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full object-cover"
      />

      {/* Bottom white fade (Figma node 1:3515) — softens the
          pattern into the bottom of the viewport so the store
          badges sit on a brighter band. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-[4] h-[40%] bg-gradient-to-t from-white via-white/40 to-transparent"
      />

      {/* Side character art (Figma 1:3512 / 1:3513). Left/right
          character art mirrors HeroSection's float-loop pattern:
          right sprite offset by -2s so the two are out of phase
          (one rising while the other settles). */}
      <img
        src="/images/hero-character-right.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-[-28%] lg:left-auto right-auto lg:right-0 top-[16%] lg:top-0 -z-[5] w-[80%] lg:w-[47%] min-h-[50vh] max-h-[95%] object-contain select-none animate-float-y"
        draggable={false}
      />
      <img
        src="/images/hero-character-left.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-[-20%] lg:right-auto left-auto lg:left-0 top-[8%] lg:top-0 -z-[5] w-[80%] lg:w-[47%] min-h-[50vh] max-h-[120%] object-contain select-none animate-float-y [animation-delay:-2s]"
        draggable={false}
      />

      {/* Foreground content column — logo, title pill, store
          badges. Sized to match HeroSection's column rhythm so
          the placeholder reads as the same "scene" with parts
          removed. */}
      <div className="relative z-10 flex w-full max-w-3xl flex-col items-center gap-6 px-4 md:gap-8 md:px-8 lg:gap-10">
        {/* Title logo (the "I LOVE COOKING" mark). */}
        <img
          src="/images/logo.png"
          alt={t('hero.title')}
          className="h-28 w-auto select-none md:h-32 lg:h-[160px]"
          draggable={false}
        />

        {/* Title pill — same cream #F6EADF lozenge HeroSection
            uses (Figma 120:2046), with mask-recoloured fork-spoon
            icons flanking the headline. Headline copy is the
            new `hero.underConstruction` key so this surface can
            tell visitors the project isn't live yet without
            relying on the registration-flavoured `hero.subtitle`
            string used by the full app. */}
        <div className="flex shrink-0 flex-col items-center justify-center gap-[10px] rounded-[100px] bg-[#F6EADF] px-1 py-2 md:px-2 md:py-4 lg:px-4 lg:py-5">
          <div className="flex w-full items-center justify-center gap-4 px-4 md:gap-4 lg:gap-5">
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 bg-section-feature md:h-6 md:w-6 lg:h-8 lg:w-8"
              style={{
                WebkitMaskImage: "url('/images/fork-spoon.svg')",
                maskImage: "url('/images/fork-spoon.svg')",
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
            <h1 className="hero-title-shadow mt-0 mb-1 text-center font-display text-2xl font-extrabold leading-none text-white md:mb-3 md:text-4xl lg:text-[48px]">
              {t('hero.underConstruction')}
            </h1>
            <span
              aria-hidden="true"
              className="h-4 w-4 shrink-0 bg-section-feature md:h-6 md:w-6 lg:h-8 lg:w-8"
              style={{
                WebkitMaskImage: "url('/images/fork-spoon.svg')",
                maskImage: "url('/images/fork-spoon.svg')",
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
          </div>
        </div>

        {/* Store badges. `href="#"` placeholders match the full
            app's HeroSection until real store URLs ship. Same
            scale-on-hover affordance for parity. */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-[39px]">
          <a
            href={appStoreUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download on the App Store"
            onClick={() => trackStoreClick('apple')}
            className="block transition hover:scale-105 active:scale-95"
          >
            <img
              src="/images/store-app-store.png"
              alt="Download on the App Store"
              className="h-12 w-auto md:h-[60px] lg:h-[70px]"
              draggable={false}
            />
          </a>
          <a
            href={googlePlayUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
            onClick={() => trackStoreClick('google')}
            className="block transition hover:scale-105 active:scale-95"
          >
            <img
              src="/images/store-google-play.png"
              alt="Get it on Google Play"
              className="h-12 w-auto md:h-[60px] lg:h-[70px]"
              draggable={false}
            />
          </a>
        </div>

        {/* Support contact — required by App Store Review Guideline 1.5
            (Developer Information). Reviewers need an easy, visible
            contact path on the Support URL; the under-construction
            page is currently that URL, so the email lives here as
            a clickable mailto link. */}
        <p className="text-center text-sm text-section-feature md:text-base">
          <span className="font-semibold">{t('hero.supportLabel')}:</span>{' '}
          <a
            href="mailto:support@gmillion.io"
            className="underline underline-offset-2 hover:opacity-80"
          >
            support@gmillion.io
          </a>
        </p>
      </div>
    </main>
  );
}
